import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authFetch } from "../services/authAPI";
import moment from "moment";
import "moment/locale/fr";
import Modal from "../components/Modal";
import Checkbox from "../components/Checkbox";

const ROLE_LABELS = {
    ROLE_ADMIN: "Administrateur",
    ROLE_MANAGER: "Manager",
    ROLE_FLEET_MANAGER: "Gestionnaire de flotte",
    ROLE_EMPLOYEE: "Employé"
};

const ROLE_CLASSES = {
    ROLE_ADMIN: "primary",
    ROLE_MANAGER: "success",
    ROLE_FLEET_MANAGER: "info",
    ROLE_EMPLOYEE: "secondary"
};

const PERMISSION_OPTIONS = [
    { value: "logistics", label: "Logistique" },
    { value: "routes", label: "Route" },
    { value: "users:manage", label: "Gestion des utilisateurs" },
    { value: "billing:read", label: "Consultation des factures" },
    { value: "credentials:manage", label: "Gestion des identifiants" },
];

const PERMISSION_LABELS = Object.fromEntries(PERMISSION_OPTIONS.map(o => [o.value, o.label]));

export default function UserPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionError, setActionError] = useState(null);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showPermissionsModal, setShowPermissionsModal] = useState(false);
    const [editPermissions, setEditPermissions] = useState([]);

    const fetchUser = useCallback(async () => {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/users/${id}`);
            if (!response.ok) {
                setError("Utilisateur introuvable.");
                return;
            }
            const data = await response.json();
            setUser(data);
        } catch (err) {
            setError("Erreur lors du chargement de l'utilisateur.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    async function handleDelete() {
        setActionError(null);
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/users/${id}`, {
                method: "DELETE",
            });
            if (response.ok) {
                setShowDeleteModal(false);
                fetchUser();
            } else {
                setActionError("Erreur lors de la suppression de l'utilisateur.");
            }
        } catch (err) {
            setActionError("Erreur lors de la suppression de l'utilisateur.");
        }
    }

    async function handleUpdatePermissions() {
        setActionError(null);
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/users/${id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ permissions: editPermissions }),
            });
            if (response.ok) {
                setShowPermissionsModal(false);
                fetchUser();
            } else {
                setActionError("Erreur lors de la mise à jour des permissions.");
            }
        } catch (err) {
            setActionError("Erreur lors de la mise à jour des permissions.");
        }
    }

    async function handleRestore() {
        setActionError(null);
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/users/${id}/restore`, {
                method: "POST",
            });
            if (response.ok) {
                fetchUser();
            } else {
                setActionError("Erreur lors de la restauration de l'utilisateur.");
            }
        } catch (err) {
            setActionError("Erreur lors de la restauration de l'utilisateur.");
        }
    }

    if (loading) {
        return (
            <section>
                <div className="card mb-4">
                    <div className="card-body py-5 px-5 text-center">
                        <p className="text-muted">Chargement...</p>
                    </div>
                </div>
            </section>
        );
    }

    if (error || !user) {
        return (
            <section>
                <div className="card mb-4">
                    <div className="card-body py-5 px-5 text-center">
                        <p className="text-danger">{error ?? "Utilisateur introuvable."}</p>
                        <button className="btn btn-link" onClick={() => navigate("/users")}>
                            Retour aux utilisateurs
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <>
            <Modal
                title="Modifier les permissions"
                body={
                    <>
                        <h6>Permissions</h6>
                        {PERMISSION_OPTIONS.map((option) => (
                            <Checkbox
                                key={option.value}
                                name={`perm-${option.value}`}
                                value={option.value}
                                label={option.label}
                                checked={editPermissions.includes(option.value)}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setEditPermissions([...editPermissions, option.value]);
                                    } else {
                                        setEditPermissions(editPermissions.filter((p) => p !== option.value));
                                    }
                                }}
                            />
                        ))}
                    </>
                }
                button={{ close: "Annuler", valid: "Enregistrer" }}
                showModal={showPermissionsModal}
                setShowModal={setShowPermissionsModal}
                onSubmit={handleUpdatePermissions}
            />
            <Modal
                title="Supprimer l'utilisateur"
                body={
                    <p>
                        Êtes-vous sûr de vouloir supprimer l'utilisateur{" "}
                        <strong>{user.firstName} {user.lastName}</strong> ?
                    </p>
                }
                button={{ close: "Annuler", valid: "Supprimer" }}
                showModal={showDeleteModal}
                setShowModal={setShowDeleteModal}
                onSubmit={handleDelete}
            />
            <section>
                {actionError && (
                    <div className="alert alert-danger" role="alert">
                        {actionError}
                    </div>
                )}
                <div className="card mb-4">
                    <div className="card-header py-3">
                        <div className="row py-3 align-items-center">
                            <div className="col-sm-6 text-center text-sm-start">
                                <button
                                    type="button"
                                    className="btn btn-light me-2"
                                    onClick={() => navigate(-1)}
                                >
                                    <i className="fas fa-arrow-left me-2"></i>
                                    Retour
                                </button>
                            </div>
                            <div className="col-sm-6 text-center text-sm-end">
                                {user.role === "ROLE_EMPLOYEE" && !user.deletedAt && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary me-2"
                                        onClick={() => {
                                            setEditPermissions(user.permissions || []);
                                            setShowPermissionsModal(true);
                                        }}
                                    >
                                        Permissions
                                        <i className="fas fa-shield-halved ms-2"></i>
                                    </button>
                                )}
                                {user.deletedAt ? (
                                    <button
                                        type="button"
                                        className="btn btn-success me-2"
                                        onClick={handleRestore}
                                    >
                                        Restaurer
                                        <i className="fas fa-rotate-left ms-2"></i>
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn btn-danger me-2"
                                        onClick={() => setShowDeleteModal(true)}
                                    >
                                        Supprimer
                                        <i className="fas fa-trash ms-2"></i>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="card-body py-5 px-5">
                        <div className="row g-4">
                            <div className="col-md-6">
                                <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Prénom</h6>
                                <p className="fw-bold mb-0">{user.firstName}</p>
                            </div>
                            <div className="col-md-6">
                                <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Nom</h6>
                                <p className="fw-bold mb-0">{user.lastName}</p>
                            </div>
                            <div className="col-md-6">
                                <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Email</h6>
                                <p className="mb-0">{user.email}</p>
                            </div>
                            <div className="col-md-6">
                                <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Rôle</h6>
                                <p className="mb-0">
                                    <span className={"badge badge-" + (ROLE_CLASSES[user.role] ?? "secondary")}>
                                        {ROLE_LABELS[user.role] ?? user.role}
                                    </span>
                                </p>
                            </div>
                            <div className="col-md-6">
                                <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Statut</h6>
                                <p className="mb-0">
                                    {user.deletedAt ? (
                                        <>
                                            <i className="fa-solid fa-circle-xmark text-danger me-2"></i>
                                            <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                                                Supprimé le {moment(user.deletedAt).locale("fr").format("dddd Do MMMM YYYY [à] HH:mm")}
                                            </span>
                                        </>
                                    ) : (
                                        <i className="fa-solid fa-circle-check text-success"></i>
                                    )}
                                </p>
                            </div>
                            {user.role === "ROLE_EMPLOYEE" && (
                                <div className="col-12">
                                    <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Permissions</h6>
                                    <div className="d-flex flex-wrap gap-2">
                                        {user.permissions?.length > 0 ? (
                                            user.permissions.map((perm) => (
                                                <span key={perm} className="badge badge-primary">
                                                    {PERMISSION_LABELS[perm] || perm}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="text-muted">Aucune permission</span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
